import { Component, computed, inject, signal, ChangeDetectionStrategy} from '@angular/core';
import { RestService } from '@cumlaude/shared-services';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { getSchooljaarTovHuidig, getSchooljarenBetween } from '@cumlaude/shared-utils';
import { RVestigingClusterVSO, VSOCluster } from '@cumlaude/service-contract';
import { Option, SingleSelectDropdownComponent } from '@cumlaude/shared-components-inputs';
import { find } from 'lodash-es';
import { ButtonComponent } from '@cumlaude/shared-components-buttons';
import { map, switchMap } from 'rxjs/operators';
import { BehaviorSubject, combineLatestWith, lastValueFrom } from 'rxjs';
import { SaveableBeheerDashboard } from '../save-guard/save-guard';

interface VestigingClusterVSO {
	rVestigingClusterVSO: RVestigingClusterVSO;
	vestigingId: string;
	vestigingNaam: string;
	origineelCluster?: VSOCluster;
	cluster?: VSOCluster;
}

@Component({
	selector: 'app-vestiging-cluster-vso',
	templateUrl: './vestiging-cluster-vso.component.html',
	styleUrls: ['./vestiging-cluster-vso.component.scss'],
	imports: [SingleSelectDropdownComponent, ButtonComponent],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VestigingClusterVSOComponent implements SaveableBeheerDashboard {
	private readonly restService = inject(RestService);

	schooljaar = signal(getSchooljaarTovHuidig());

	schooljaarOptions = signal(getSchooljarenBetween('2022/2023', getSchooljaarTovHuidig()).map((schooljaar) => new Option(schooljaar)));

	private readonly refresh$ = new BehaviorSubject<void>(undefined);
	private readonly schooljaarObservable = toObservable(this.schooljaar);

	vestigingClusterVSOs$ = this.refresh$.pipe(
		switchMap(() => this.schooljaarObservable),
		switchMap((schooljaar) => this.restService.getVestigingClusterVSOs({ schooljaar })),
		combineLatestWith(this.restService.getVestigingen({ cumlaudeActief: true, vso: true })),
		map(([vestigingClusterVSOs, vestigingen]) => {
			return vestigingClusterVSOs
				.filter((vestigingClusterVso) => vestigingen.some((v) => v.vestigingId === vestigingClusterVso.vestigingId))
				.map((vestigingClusterVso) => {
					return <VestigingClusterVSO>{
						rVestigingClusterVSO: vestigingClusterVso,
						vestigingId: vestigingClusterVso.vestigingId,
						vestigingNaam: vestigingen.find((v) => v.vestigingId === vestigingClusterVso.vestigingId)!.naam,
						origineelCluster: vestigingClusterVso.cluster,
						cluster: vestigingClusterVso.cluster,
					};
				})
				.sort((a, b) => a.vestigingNaam.localeCompare(b.vestigingNaam));
		}),
		takeUntilDestroyed()
	);

	vestigingClusterVSOs = signal<VestigingClusterVSO[]>([]);

	dirty = computed(() => {
		const vestigingClusterVSOs = this.vestigingClusterVSOs();
		return vestigingClusterVSOs.some((vestigingClusterVso) => vestigingClusterVso.cluster !== vestigingClusterVso.origineelCluster);
	});

	clusterOptions = signal(Object.values(VSOCluster).map((cluster) => new Option(cluster, this.clusterString(cluster))));

	constructor() {
		this.vestigingClusterVSOs$.subscribe((vestigingClusterVSOs) => {
			this.vestigingClusterVSOs.set(vestigingClusterVSOs);
		});
	}

	clusterString(cluster: VSOCluster): string {
		if (cluster === VSOCluster.CLUSTER_3_4) return 'Cluster 3/4';
		else if (cluster === VSOCluster.CLUSTER_2_EMB) return 'Cluster 2 + EMB';

		const clusterName = cluster.toLowerCase().replace('_', ' ');
		return clusterName.charAt(0).toUpperCase() + clusterName.slice(1);
	}

	getSelectedCluster(cluster?: VSOCluster | undefined): Option<VSOCluster> {
		return find(this.clusterOptions(), (option) => option.value === cluster)!;
	}

	getSelectedSchooljaar() {
		return find(this.schooljaarOptions(), (option) => option.value === this.schooljaar())!;
	}

	updateCluster(selected: VestigingClusterVSO, cluster: VSOCluster) {
		const vestigingClusterVSOs = this.vestigingClusterVSOs();
		const vestigingClusterVSO = vestigingClusterVSOs.find((v) => v.vestigingId === selected.vestigingId)!;
		vestigingClusterVSO.cluster = cluster;
		this.vestigingClusterVSOs.set([...vestigingClusterVSOs]);
	}

	revert() {
		const vestigingClusterVSOs = this.vestigingClusterVSOs();
		vestigingClusterVSOs
			.filter((vestigingClusterVso) => vestigingClusterVso.cluster !== vestigingClusterVso.origineelCluster)
			.forEach((vestigingClusterVso) => {
				vestigingClusterVso.cluster = vestigingClusterVso.origineelCluster;
			});
		this.vestigingClusterVSOs.set([...vestigingClusterVSOs]);
	}

	async trySave() {
		const modified = this.vestigingClusterVSOs().filter(
			(vestigingClusterVso) => vestigingClusterVso.cluster !== vestigingClusterVso.origineelCluster
		);
		const schooljaar = this.schooljaar();
		for (const vestigingClusterVso of modified) {
			await lastValueFrom(
				this.restService.putVestigingClusterVSO({
					...vestigingClusterVso.rVestigingClusterVSO,
					cluster: vestigingClusterVso.cluster,
				})
			);

			const others = await lastValueFrom(
				this.restService.getVestigingClusterVSOs({ schooljaarNa: schooljaar, vestigingId: vestigingClusterVso.vestigingId })
			);
			for (const other of others) {
				if (other.cluster) break;

				other.cluster = vestigingClusterVso.cluster;
				await lastValueFrom(this.restService.putVestigingClusterVSO(other));
			}
		}
		this.refresh$.next();
	}
}
